import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { PAGE_SIZES } from 'constants/table';

import { useQueryString } from './useQueryString';

interface ISortProps {
  sortBy: string;
  sortDirection: 'asc' | 'desc';
}

export const usePaging = (
  sizeOptions: number[] = PAGE_SIZES,
  defaultSort: string[] = []
) => {
  const { updateQueryString } = useQueryString();
  const [searchParams] = useSearchParams();
  const paging = useMemo(() => {
    const queryParamSort = searchParams.get('sort');
    let parsedQueryParamSort = queryParamSort
      ? queryParamSort.split(',')
      : defaultSort;
    parsedQueryParamSort =
      parsedQueryParamSort.length === 2 ? parsedQueryParamSort : [];
    return {
      page: searchParams.get('page') || 0,
      size: searchParams.get('size') || sizeOptions[0],
      sort: parsedQueryParamSort,
    };
  }, [searchParams, sizeOptions, defaultSort]);

  const handleChangePage = useCallback(
    (newPage: number) => {
      updateQueryString({ ...paging, page: newPage }, true);
    },
    [paging, updateQueryString]
  );

  const handleChangeRowsPerPage = useCallback(
    (size = 25) => {
      updateQueryString(
        {
          ...paging,
          page: 0,
          size,
        },
        true
      );
    },
    [paging, updateQueryString]
  );

  const handleChangeSort = useCallback(
    (sort: string[]) => {
      updateQueryString(
        {
          ...paging,
          page: 0,
          sort,
        },
        true
      );
    },
    [paging, updateQueryString]
  );

  const sortInfo = useMemo((): ISortProps => {
    const [sortBy, sortDirection] = paging.sort;
    return {
      sortBy,
      sortDirection: sortDirection === 'asc' ? 'asc' : 'desc',
    };
  }, [paging.sort]);

  return {
    paging,
    sortInfo,
    handleChangePage,
    handleChangeRowsPerPage,
    handleChangeSort,
  };
};
